/**
 * Vindaa wishlist
 * ----------------
 * A small, dependency-free wishlist built on localStorage (Shopify has no
 * native wishlist, and this avoids needing a customer account or an app).
 *
 * What's stored: an array of product HANDLES, e.g. ["gold-hoop-earrings"].
 * Handles (not full product data) are stored on purpose — the wishlist page
 * looks each one up live via /products/{handle}.js, so price, stock, and
 * images are always current even if they change after something was saved.
 *
 * Load this file on EVERY page (theme.liquid), not just the product page,
 * so the header badge count is correct everywhere. See the setup notes
 * that came with this file for exactly where to add it.
 *
 * Public API (window.VindaaWishlist):
 *   .get()            -> array of handles currently saved
 *   .has(handle)      -> boolean
 *   .add(handle)      -> adds, returns updated array
 *   .remove(handle)   -> removes, returns updated array
 *   .toggle(handle)   -> adds if absent / removes if present, returns the new "is active" boolean
 *   .initButtons(root)-> (re)binds any [data-wishlist-toggle] buttons under `root` (default: document).
 *                        Call this yourself after injecting new wishlist buttons via JS
 *                        (e.g. inside a dynamically-rendered product card).
 *
 * Event: document dispatches "wishlist:updated" with { detail: { count, handles } }
 * whenever the list changes — listen for this to keep any UI (badges, pages) in sync.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'vindaa:wishlist';

  // ---------------------------------------------------------------------
  // storage
  // ---------------------------------------------------------------------

  function getWishlist() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      // localStorage can throw in private/incognito modes in some browsers
      return [];
    }
  }

  function saveWishlist(list) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      // fail silently — worst case the wishlist just doesn't persist
    }
    document.dispatchEvent(
      new CustomEvent('wishlist:updated', { detail: { count: list.length, handles: list } })
    );
  }

  function isInWishlist(handle) {
    return getWishlist().indexOf(handle) !== -1;
  }

  function addToWishlist(handle) {
    var list = getWishlist();
    if (list.indexOf(handle) === -1) {
      list.push(handle);
      saveWishlist(list);
    }
    return list;
  }

  function removeFromWishlist(handle) {
    var list = getWishlist().filter(function (h) {
      return h !== handle;
    });
    saveWishlist(list);
    return list;
  }

  function toggleWishlist(handle) {
    if (isInWishlist(handle)) {
      removeFromWishlist(handle);
      return false;
    }
    addToWishlist(handle);
    return true;
  }

  window.VindaaWishlist = {
    get: getWishlist,
    has: isInWishlist,
    add: addToWishlist,
    remove: removeFromWishlist,
    toggle: toggleWishlist
  };

  // ---------------------------------------------------------------------
  // one-time injected styles (toast + count badge) so this file works as a
  // single drop-in include, without needing extra CSS wired up elsewhere
  // ---------------------------------------------------------------------

  var style = document.createElement('style');
  style.textContent =
    '.vindaa-wishlist-toast{position:fixed;left:50%;bottom:24px;transform:translate(-50%,12px);' +
    'background:#1A1A1A;color:#fff;padding:12px 22px;border-radius:999px;font-size:14px;font-weight:600;' +
    'font-family:inherit;opacity:0;pointer-events:none;z-index:10000;' +
    'transition:opacity .25s ease,transform .25s ease;box-shadow:0 8px 24px rgba(0,0,0,.2);text-align:center;' +
    'max-width:86vw;}' +
    '.vindaa-wishlist-toast.is-visible{opacity:1;transform:translate(-50%,0);}';
  document.head.appendChild(style);

  var toastTimer;
  function showToast(message) {
    var toast = document.getElementById('vindaa-wishlist-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'vindaa-wishlist-toast';
      toast.className = 'vindaa-wishlist-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 2200);
  }

  // ---------------------------------------------------------------------
  // bind any [data-wishlist-toggle] buttons found on the page
  //
  // Expected attributes on the button:
  //   data-product-handle  (required)  the product's handle
  //   data-product-title   (optional)  used in the toast message
  //   data-add-label       (optional)  aria-label / title when inactive
  //   data-remove-label    (optional)  aria-label / title when active
  // ---------------------------------------------------------------------

  function setButtonState(btn, active) {
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    var label = active
      ? btn.getAttribute('data-remove-label') || 'Remove from wishlist'
      : btn.getAttribute('data-add-label') || 'Add to wishlist';
    btn.setAttribute('aria-label', label);
    btn.title = label;

    // Icon-only buttons (like the one on the product image) don't have
    // this — buttons with a visible text label (like the full-width buy
    // box button) mark it with [data-wishlist-label] so it can be swapped.
    var labelEl = btn.querySelector('[data-wishlist-label]');
    if (labelEl) labelEl.textContent = label;
  }

  function initButtons(scope) {
    var root = scope || document;
    var buttons = root.querySelectorAll('[data-wishlist-toggle]');
    buttons.forEach(function (btn) {
      var handle = btn.getAttribute('data-product-handle');
      if (!handle || btn.getAttribute('data-wishlist-bound') === 'true') return;
      btn.setAttribute('data-wishlist-bound', 'true');

      setButtonState(btn, isInWishlist(handle));

      btn.addEventListener('click', function (e) {
        // stopPropagation matters here: on the product page this button
        // sits inside the zoomable main image, which has its own click
        // handler for tap-to-zoom on touch devices.
        e.preventDefault();
        e.stopPropagation();

        var nowActive = toggleWishlist(handle);
        setButtonState(btn, nowActive);

        var title = btn.getAttribute('data-product-title') || 'Item';
        showToast(nowActive ? title + ' added to your wishlist' : title + ' removed from your wishlist');
      });
    });
  }

  // keep any button referencing this handle in sync if the wishlist
  // changes elsewhere (another button, another tab, the wishlist page)
  document.addEventListener('wishlist:updated', function () {
    document.querySelectorAll('[data-wishlist-toggle][data-wishlist-bound="true"]').forEach(function (btn) {
      var handle = btn.getAttribute('data-product-handle');
      if (handle) setButtonState(btn, isInWishlist(handle));
    });
  });

  window.VindaaWishlist.initButtons = initButtons;

  // ---------------------------------------------------------------------
  // header badge(s): any element with [data-wishlist-count] gets the
  // current wishlist count kept in sync automatically
  // ---------------------------------------------------------------------

  function updateCountBadges(count) {
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count;
      el.classList.toggle('is-hidden', count === 0);
    });
  }

  document.addEventListener('wishlist:updated', function (e) {
    updateCountBadges(e.detail.count);
  });

  document.addEventListener('DOMContentLoaded', function () {
    initButtons(document);
    updateCountBadges(getWishlist().length);
  });
})();